import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { PasswordValidation, validatePassword } from '../../components/Auth/PasswordValidation';
import { supabase } from '../../lib/supabase';
import { PolicyAcceptance } from '../../components/policies/PolicyAcceptance';
import { policyService } from '../../services/policyService';

const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [showPolicyError, setShowPolicyError] = useState(false);
  const { user, login, register, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Debug: Loading durumunu göster ve manual sıfırlama imkanı
  const [loadingTime, setLoadingTime] = useState(0);
  
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      setLoadingTime(0);
      interval = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
    } else {
      setLoadingTime(0);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Eğer kullanıcı zaten giriş yapmışsa dashboard'a yönlendir
  if (user) {
    const dashboardPath = user.role === 'admin' ? '/dashboard/admin' : '/dashboard';
    return <Navigate to={dashboardPath} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShowEmailVerification(false);

    if (!isLogin) {
      // Register logic
      if (password !== confirmPassword) {
        setError(t('auth.passwordMismatch'));
        return;
      }

      // Güçlü şifre validasyonu
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        setError('Şifre güvenlik gereksinimlerini karşılamıyor: ' + passwordValidation.feedback.join(', '));
        return;
      }

      // Check if policies are accepted
      if (!policiesAccepted) {
        setError(t('auth.policiesRequired') || 'Devam etmek için tüm politikaları kabul etmelisiniz.');
        setShowPolicyError(true);
        return;
      }

      // Register with Supabase
      const result = await register(email, password, 'artist', firstName, lastName);

      if (result.success) {
        // Save policy acceptances after successful registration
        // We'll save them after the user verifies their email and logs in
        // Store in localStorage temporarily
        if (policiesAccepted) {
          localStorage.setItem('pending_policy_acceptance', JSON.stringify({
            email,
            policies: ['terms_of_use', 'privacy_policy', 'cookie_policy', 'gdpr_compliance', 'antifraud_policy'],
            timestamp: Date.now()
          }));
        }
        if (result.needsVerification) {
          setSuccess(result.message === 'EMAIL_VERIFICATION_REQUIRED' ? t('messages.emailVerificationMessage') : result.message || t('messages.registerSuccess'));
          setShowEmailVerification(true);
          // 3 saniye sonra login sekmesine geç
          setTimeout(() => {
            setIsLogin(true);
            setShowEmailVerification(false);
          }, 3000);
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(result.message || t('auth.registerError'));
      }
      return;
    }

    // Login logic
    const result = await login(email, password);

    if (result.success) {
      // Check for pending policy acceptances
      const pendingAcceptance = localStorage.getItem('pending_policy_acceptance');
      if (pendingAcceptance) {
        try {
          const data = JSON.parse(pendingAcceptance);
          // Check if it's for the same email and not too old (24 hours)
          if (data.email === email && (Date.now() - data.timestamp) < 86400000) {
            // Get current user from auth context after successful login
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser) {
              await policyService.acceptPolicies(currentUser.id, data.policies);
              localStorage.removeItem('pending_policy_acceptance');
            }
          }
        } catch (error) {
          console.error('Error processing policy acceptance:', error);
          localStorage.removeItem('pending_policy_acceptance');
        }
      }
      // Navigate component'i user state'i set edildikten sonra otomatik olarak çalışacak
    } else {
      setError(result.message || t('auth.invalidCredentials'));
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!resetEmail.trim()) {
      setError('Email is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (error) {
        console.error('Password reset error:', error);
        setError(error.message || 'Failed to send reset email');
        return;
      }

      setSuccess('Password reset email sent! Please check your inbox.');
      
      // Hide forgot password form after successful submission
      setTimeout(() => {
        setShowForgotPassword(false);
        setSuccess('');
        setResetEmail('');
      }, 3000);
      
    } catch (error: any) {
      console.error('Password reset exception:', error);
      setError(error.message || 'An error occurred while sending reset email');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <button 
              onClick={handleBackToHome}
              className="mb-4 text-white/70 hover:text-white transition-colors text-sm flex items-center mx-auto"
            >
              ← {t('auth.backToHome')}
            </button>
            <div className="flex items-center justify-center mb-4">
              <img src="/assest/logo.png" alt="Distnary" className="h-32 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {isLogin ? t('auth.loginToAccount') : t('auth.createNewAccount')}
            </h1>
            <p className="text-white/70">
              {isLogin ? t('auth.continueJourney') : t('auth.startCareer')}
            </p>
          </div>

          <div className="flex mb-6">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-l-lg transition-all ${
                isLogin 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {t('auth.signIn')}
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-r-lg transition-all ${
                !isLogin 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {t('auth.signUp')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-white/80 mb-2">
                    {t('auth.firstName')}
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('auth.firstName')}
                    required={!isLogin}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-white/80 mb-2">
                    {t('auth.lastName')}
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('auth.lastName')}
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="example@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
              
              {/* Password Validation Component - sadece register modunda göster */}
              {!isLogin && (
                <PasswordValidation 
                  password={password} 
                  showValidation={password.length > 0} 
                />
              )}
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="••••••••"
                  required={!isLogin}
                />
              </div>
            )}

            {/* Policy Acceptance for Registration */}
            {!isLogin && (
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <PolicyAcceptance
                  onAccept={(accepted) => {
                    setPoliciesAccepted(accepted);
                    if (accepted) setShowPolicyError(false);
                  }}
                  language="tr"
                  showError={showPolicyError}
                />
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg mb-4">
                {success}
              </div>
            )}

            {/* Email verification reminder */}
            {showEmailVerification && (
              <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-medium">Email kutunuzu kontrol edin!</p>
                    <p className="text-sm">Doğrulama linkine tıkladıktan sonra giriş yapabilirsiniz.</p>
                    <p className="text-xs mt-1">3 saniye sonra giriş sekmesine yönlendirileceksiniz...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Debug: Loading stuck warning */}
            {isLoading && loadingTime > 5 && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div>
                      <p className="font-medium">{t('messages.loadingTooLong', { time: loadingTime })}</p>
                      <p className="text-sm">{t('messages.checkConsoleErrors')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm"
                  >
                    {t('messages.refresh')}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('common.loading')}
                </div>
              ) : (
                isLogin ? t('auth.signIn') : t('auth.signUp')
              )}
            </button>

            {/* Forgot Password Link */}
            {isLogin && !showForgotPassword && (
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-white/70 hover:text-orange-400 transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </form>

          {/* Forgot Password Form */}
          {showForgotPassword && (
            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-white">Reset Password</h3>
                <p className="text-sm text-white/70 mt-1">
                  Enter your email address and we'll send you a reset link
                </p>
              </div>
              
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setError('');
                      setSuccess('');
                    }}
                    className="flex-1 px-4 py-2 text-white/70 border border-white/20 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Send Reset Link
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login; 